import React, { useEffect, useState } from "react";
import api, { STAGE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, FolderKanban, CheckCircle2, Clock, AlertTriangle, Users, FileText, Bell, Mail, IndianRupee, DollarSign, ArrowUp, ArrowDown, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import SurveydraftsActivityPanel from "@/components/dashboard/SurveydraftsActivityPanel";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import { StatCard, MiniStat } from "@/components/dashboard/StatTiles";
import { ForecastRevenueTile } from "@/components/dashboard/ForecastRevenueTile";
import { TeamPerformanceTile } from "@/components/dashboard/TeamPerformanceTile";
import { WeeklyDigestTile } from "@/components/dashboard/WeeklyDigestTile";
import { OutstandingTile, CashRunwayTile, StaleJobsTile } from "@/components/dashboard/OpsTiles";
import { JobsByPeriodSection } from "@/components/dashboard/JobsByPeriodSection";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || (user?.team_role || "").toLowerCase() === "admin";
  const teamRole = (user?.team_role || "").toLowerCase();
  const isHr = !isAdmin && (teamRole === "hr" || (user?.role || "").toLowerCase() === "hr_manager");
  const isBareEmployee = !isAdmin && !isHr && (teamRole === "employee" || teamRole === "" || teamRole === "member");
  // Bare employees and HR both get the personal "My Space" view — revenue,
  // billing and full-company pipeline stay hidden. Managers/leads/admins keep
  // the full operational dashboard below. HR uses the /documentation and
  // /users pages for their day-to-day work.
  if (isBareEmployee || isHr) return <EmployeeDashboard />;
  return <AdminOpsDashboard />;
}

function AdminOpsDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || (user?.team_role || "").toLowerCase() === "admin";
  const teamRole = (user?.team_role || "").toLowerCase();
  const showTeamPerf = isAdmin || teamRole === "manager" || teamRole === "lead";
  const canScope = teamRole === "manager" || teamRole === "lead" || isAdmin;
  // Non-admin managers/leads default to their team; admins default to company-wide.
  const [scope, setScope] = useState(canScope && !isAdmin ? "my_team" : "company");
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState({ clients: [], total_revenue: 0, month: "" });
  const [reminders, setReminders] = useState(null);
  const [revMoM, setRevMoM] = useState(null);
  const [utilMoM, setUtilMoM] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [teamPerf, setTeamPerf] = useState(null);
  const [runway, setRunway] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [stale, setStale] = useState(null);
  const [ewmaAlpha, setEwmaAlpha] = useState(0.5);
  const [chartMonths, setChartMonths] = useState(6);

  useEffect(() => {
    api.get("/stats/dashboard", { params: { scope } }).then(({ data }) => setStats(data)).catch(() => {});
    if (isAdmin) {
      api.get("/clients/revenue/monthly").then(({ data }) => setRevenue(data)).catch(() => {});
      api.get("/invoices/reminders/analytics").then(({ data }) => setReminders(data)).catch(() => {});
      api.get("/stats/cash-runway").then(({ data }) => setRunway(data)).catch(() => {});
      api.get("/stats/outstanding").then(({ data }) => setOutstanding(data)).catch(() => {});
    }
    // Stale jobs is readable by every authenticated user (not admin-only)
    api.get("/jobs/stale").then(({ data }) => setStale(data)).catch(() => {});
    if (showTeamPerf) {
      api.get("/team/performance").then(({ data }) => setTeamPerf(data)).catch(() => {});
    }
  }, [isAdmin, showTeamPerf, scope]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/stats/client-revenue-monthly", { params: { months: chartMonths } }).then(({ data }) => setRevMoM(data)).catch(() => {});
    api.get("/stats/utilization-monthly", { params: { months: chartMonths } }).then(({ data }) => setUtilMoM(data)).catch(() => {});
  }, [chartMonths, isAdmin]);

  // Forecast — debounced re-fetch when the EWMA alpha slider moves
  useEffect(() => {
    if (!isAdmin) return;
    const t = setTimeout(() => {
      api.get("/stats/forecasted-revenue", { params: { months: 12, ewma_alpha: ewmaAlpha } })
        .then(({ data }) => setForecast(data))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [ewmaAlpha, isAdmin]);

  const stageData = stats?.stages_count
    ? Object.entries(stats.stages_count).map(([k, v]) => ({ stage: STAGE_LABELS[k] || k, count: v }))
    : [];

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-mono">WELCOME BACK TO LAND DRAFTERS</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-none mt-1">
            {user?.name || "Operator"}.
          </h1>
          <p className="text-sm text-zinc-600 mt-2 font-mono">
            {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 brut-card px-4 py-2">
          <div className="w-2 h-2 bg-green-500"></div>
          <span className="label-mono">ALL SYSTEMS NOMINAL</span>
        </div>
      </div>

      {canScope && (
        <div className="flex items-center gap-2" data-testid="dashboard-scope-toggle">
          <div className="label-mono">SCOPE</div>
          <div className="inline-flex border border-zinc-900">
            <button
              onClick={() => isAdmin && setScope("company")}
              disabled={!isAdmin}
              className={"px-3 py-1 text-[11px] font-mono font-bold " + (scope === "company" ? "bg-zinc-900 text-white" : "bg-white text-zinc-900 hover:bg-zinc-100") + (!isAdmin ? " opacity-40 cursor-not-allowed" : "")}
              data-testid="scope-company"
              title={isAdmin ? "Show company-wide stats" : "Only admins can see company-wide stats"}
            >COMPANY</button>
            <button
              onClick={() => setScope("my_team")}
              className={"px-3 py-1 text-[11px] font-mono font-bold border-l border-zinc-900 " + (scope === "my_team" ? "bg-zinc-900 text-white" : "bg-white text-zinc-900 hover:bg-zinc-100")}
              data-testid="scope-my-team"
            >MY TEAM {stats?.team_member_count ? `(${stats.team_member_count})` : ""}</button>
          </div>
          {!isAdmin && (
            <span className="text-[10px] font-mono text-zinc-500">Non-admins are scoped to their team only.</span>
          )}
          {scope === "my_team" && stats?.team_member_count === 1 && isAdmin && (
            <span className="text-[10px] font-mono text-amber-700">Only your own data — add reports via /users to see team stats.</span>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="TOTAL JOBS" value={stats?.total_jobs ?? "—"} icon={FolderKanban} accent="bg-zinc-200" testid="stat-total-jobs" />
        <StatCard label="IN PROGRESS" value={stats?.in_progress ?? "—"} icon={Clock} accent="bg-blue-200" testid="stat-in-progress" />
        <StatCard label="COMPLETED" value={stats?.completed ?? "—"} icon={CheckCircle2} accent="bg-green-200" testid="stat-completed" />
        <StatCard label="ON HOLD" value={stats?.hold ?? "—"} icon={AlertTriangle} accent="bg-amber-200" testid="stat-hold" />
      </div>

      {/* Job buckets: All / Today / Yesterday — counts + expandable client breakdown */}
      <JobsByPeriodSection />

      {/* Surveydrafts.us live activity & divergence */}
      <SurveydraftsActivityPanel />

      {/* Cash-flow runway + Stale Jobs strip (admin only for runway, everyone for stale) */}
      {(isAdmin || stale) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {isAdmin && runway && <CashRunwayTile data={runway} />}
          {isAdmin && outstanding && <OutstandingTile data={outstanding} />}
          {stale && stale.count > 0 && <StaleJobsTile data={stale} />}
          {isAdmin && <WeeklyDigestTile />}
        </div>
      )}

      {/* Forecasted Revenue (USD, 3-mo moving avg) — admin only */}
      {isAdmin && forecast && (
        <ForecastRevenueTile data={forecast} ewmaAlpha={ewmaAlpha} onAlphaChange={setEwmaAlpha} />
      )}

      {/* My Team's Performance — managers / leads / admin */}
      {showTeamPerf && teamPerf && teamPerf.headcount > 0 && (
        <TeamPerformanceTile data={teamPerf} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="brut-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono">EFFICIENCY TREND</div>
              <div className="font-heading text-xl font-bold">Last 14 days · Given vs Taken</div>
            </div>
            <div className="bg-yellow-200 border border-zinc-900 px-3 py-1 font-mono text-xs font-bold">
              AVG {stats?.avg_efficiency ?? 0}%
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.trend || []}>
                <CartesianGrid stroke="#E4E4E7" strokeDasharray="0" />
                <XAxis dataKey="date" stroke="#52525B" fontSize={10} tick={{ fontFamily: 'IBM Plex Mono' }} />
                <YAxis stroke="#52525B" fontSize={10} tick={{ fontFamily: 'IBM Plex Mono' }} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #18181B", borderRadius: 0, fontFamily: 'IBM Plex Mono', fontSize: 12 }} />
                <Line type="monotone" dataKey="efficiency" stroke="#2563EB" strokeWidth={2.5} dot={{ fill: "#18181B", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="brut-card p-5">
          <div className="label-mono mb-3">PIPELINE</div>
          <div className="space-y-2">
            {stageData.map((s) => (
              <div key={s.stage} className="flex items-center justify-between text-sm">
                <span className="font-mono">{s.stage}</span>
                <div className="flex items-center gap-2 flex-1 ml-3">
                  <div className="flex-1 h-3 bg-zinc-100 border border-zinc-900 relative">
                    <div className="absolute inset-y-0 left-0 bg-zinc-900" style={{ width: `${Math.min(100, s.count * 10)}%` }} />
                  </div>
                  <span className="font-mono font-bold w-6 text-right">{s.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="TEAM SIZE" value={stats?.total_users ?? "—"} icon={Users} accent="bg-indigo-200" testid="stat-users" />
        <StatCard label="FILES STORED" value={stats?.total_files ?? "—"} icon={FileText} accent="bg-orange-200" testid="stat-files" />
        <StatCard label="OPEN REMINDERS" value={stats?.open_reminders ?? "—"} icon={Bell} accent="bg-pink-200" testid="stat-reminders" />
        <StatCard label="SENT TO CLIENT" value={stats?.sent_to_client ?? "—"} icon={TrendingUp} accent="bg-emerald-200" testid="stat-sent" />
      </div>

      {/* Reminder Analytics — ROI of the invoice reminders feature */}
      {reminders && (reminders.reminders_sent_this_month > 0 || reminders.pending_count > 0) && (
        <div className="brut-card p-5" data-testid="reminder-analytics-widget">
          <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="label-mono">FOLLOW-UP IMPACT · {reminders.month}</div>
              <div className="font-heading text-xl font-bold">Reminders driving payments</div>
            </div>
            {reminders.pending_count > 0 && (
              <div className="bg-orange-200 border border-zinc-900 px-3 py-1 font-mono text-xs font-bold flex items-center gap-1.5">
                <Bell className="w-3.5 h-3.5" /> {reminders.pending_count} PENDING
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MiniStat
              label="REMINDERS SENT"
              value={reminders.reminders_sent_this_month}
              delta={reminders.reminders_sent_this_month - reminders.reminders_sent_last_month}
              icon={Mail}
              accent="bg-blue-200"
              testid="metric-reminders-sent"
            />
            <MiniStat
              label="RECOVERED (7 DAYS)"
              value={reminders.recoveries_within_7_days}
              icon={CheckCircle2}
              accent="bg-green-200"
              testid="metric-recoveries"
            />
            <MiniStat
              label="₹ RECOVERED"
              value={`₹${reminders.total_recovered_inr.toLocaleString("en-IN")}`}
              icon={IndianRupee}
              accent="bg-yellow-200"
              testid="metric-recovered-inr"
              isMoney
            />
            <MiniStat
              label="LAST MONTH"
              value={reminders.reminders_sent_last_month}
              icon={Clock}
              accent="bg-zinc-200"
              testid="metric-last-month"
            />
          </div>

          {/* Breakdown by kind */}
          {Object.keys(reminders.by_kind || {}).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2 text-xs font-mono">
              {Object.entries(reminders.by_kind).map(([k, v]) => (
                <span key={k} className="border border-zinc-900 bg-zinc-50 px-2 py-1">
                  {k.replaceAll("_", " ").toUpperCase()}: <b>{v}</b>
                </span>
              ))}
            </div>
          )}

          {/* Top recoveries */}
          {reminders.top_recoveries?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-zinc-200">
              <div className="label-mono mb-2">TOP RECOVERIES THIS MONTH</div>
              <div className="space-y-1.5">
                {reminders.top_recoveries.map((r) => (
                  <div key={r.invoice_id} className="flex items-center justify-between text-xs font-mono border border-zinc-200 px-2 py-1.5 hover:bg-zinc-50">
                    <div className="truncate">
                      <span className="font-bold">{r.invoice_number}</span>
                      <span className="text-zinc-500"> · {r.client_name}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-green-700 font-bold">₹{r.amount_inr.toLocaleString("en-IN")}</span>
                      <span className="text-zinc-500">paid in {r.days_to_payment}d</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Top Clients This Month */}
      {revenue.clients?.length > 0 && (
        <div className="brut-card p-5" data-testid="top-clients-widget">
          <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="label-mono">TOP CLIENTS · {revenue.month}</div>
              <div className="font-heading text-xl font-bold">Revenue this month</div>
            </div>
            <div className="bg-green-200 border border-zinc-900 px-3 py-1 font-mono text-xs font-bold">
              TOTAL: {revenue.total_revenue?.toLocaleString()}
            </div>
          </div>
          <div className="space-y-2">
            {revenue.clients.slice(0, 5).map((c) => {
              const pct = revenue.total_revenue > 0 ? (c.revenue / revenue.total_revenue) * 100 : 0;
              return (
                <div key={c.client_name} className="flex items-center gap-3 text-sm">
                  <div className="w-32 truncate font-bold">{c.client_name}</div>
                  <div className="flex-1 h-5 bg-zinc-100 border border-zinc-900 relative">
                    <div className="absolute inset-y-0 left-0 bg-zinc-900" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="font-mono text-xs w-32 text-right">
                    <span className="font-bold">{c.revenue.toLocaleString()}</span>
                    <span className="text-zinc-500"> · {c.invoice_count} inv · {c.job_count} jobs</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Month-over-Month Charts */}
      {isAdmin && (
      <div className="brut-card p-5" data-testid="mom-charts-widget">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="label-mono flex items-center gap-1.5"><BarChart3 className="w-3 h-3" /> TRENDS · LAST {chartMonths} MONTHS</div>
            <div className="font-heading text-xl font-bold">Revenue & Utilization Over Time</div>
          </div>
          <div className="flex gap-1">
            {[3, 6, 12].map((n) => (
              <button key={n} onClick={() => setChartMonths(n)}
                className={`font-mono text-xs px-2.5 py-1 border border-zinc-900 ${chartMonths === n ? "bg-yellow-300 font-bold" : "bg-white"}`}
                data-testid={`mom-range-${n}`}>{n}M</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Client Revenue M-o-M */}
          <div className="border border-zinc-900 p-3">
            <div className="label-mono mb-2 flex items-center gap-1.5"><IndianRupee className="w-3 h-3" /> CLIENT REVENUE (₹)</div>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={revMoM?.series || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" fontSize={10} tickFormatter={(m) => m?.slice(5)} />
                <YAxis fontSize={10} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip contentStyle={{ border: "2px solid #18181B", borderRadius: 0, fontSize: 11, fontFamily: "monospace" }} formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }} />
                {(revMoM?.clients || []).slice(0, 5).map((c, i) => (
                  <Bar key={c} dataKey={c} stackId="rev" fill={["#18181B", "#FBBF24", "#10B981", "#EF4444", "#3B82F6"][i % 5]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
            {(!revMoM?.clients || revMoM.clients.length === 0) && (
              <div className="text-center text-zinc-400 font-mono text-xs py-6">NO REVENUE DATA IN WINDOW</div>
            )}
          </div>
          {/* Technician Utilization M-o-M */}
          <div className="border border-zinc-900 p-3">
            <div className="label-mono mb-2 flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> TECH UTILIZATION (%)</div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={utilMoM?.series || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                <XAxis dataKey="month" fontSize={10} tickFormatter={(m) => m?.slice(5)} />
                <YAxis fontSize={10} domain={[0, 'dataMax + 20']} tickFormatter={(v) => `${v}%`} />
                <Tooltip contentStyle={{ border: "2px solid #18181B", borderRadius: 0, fontSize: 11, fontFamily: "monospace" }} formatter={(v) => `${v}%`} />
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace" }} />
                {(utilMoM?.users || []).map((u, i) => (
                  <Line key={u} type="monotone" dataKey={u} stroke={["#18181B", "#FBBF24", "#10B981", "#EF4444", "#3B82F6", "#A855F7", "#F97316", "#06B6D4"][i % 8]} strokeWidth={2} dot={{ r: 3 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
            {(!utilMoM?.users || utilMoM.users.length === 0) && (
              <div className="text-center text-zinc-400 font-mono text-xs py-6">NO UTILIZATION DATA IN WINDOW</div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

